import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, CheckCircle2, XCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { KvTable, KvRow } from '../ui/KvTable';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { problemMessage, profileProblem } from './profileProblem';
import { PaneIcon } from './TerminalPaneHeader';
import { lettermarkFor, normalizeProfile, validateProfile } from './terminalProfiles';
import ProfileMcpSection from './ProfileMcpSection';

async function pick(directory) {
  try {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const p = await open({ directory, multiple: false });
    return typeof p === 'string' ? p : null;
  } catch {
    return null;
  }
}

const withTrailingRow = (env) => (env.length && !env[env.length - 1].name && !env[env.length - 1].value ? env : [...env, { name: '', value: '' }]);

/**
 * Create / edit a terminal profile. Built-ins: only arguments, working
 * directory, environment and the MCP switch are editable (the command and
 * name are fixed). Environment values are PLAIN text the user types, stored
 * in the device-local terminal config — the editor says so; secrets from the
 * Vault are deliberately not offered here.
 */
export default function ProfileEditorModal({ profile, onClose }) {
  const { t } = useTranslation();
  const isNew = !profile?.id;
  // normalizeProfile fills a missing name with a placeholder; a NEW profile
  // starts with an empty field instead.
  const [draft, setDraft] = useState(() => ({
    ...normalizeProfile(profile || { id: `u_${Date.now().toString(36)}`, command: '', cwd: { mode: 'inherit' } }),
    ...(profile ? {} : { name: '' }),
  }));
  const [argsText, setArgsText] = useState((profile?.args || []).join('\n'));
  const [env, setEnv] = useState(() => withTrailingRow(profile?.env || []));
  const [touched, setTouched] = useState(false);
  const detection = useTerminalConfigStore((s) => s.detection);
  const detectionProblems = useTerminalConfigStore((s) => s.detectionProblems) || {};
  const upsert = useTerminalConfigStore((s) => s.upsertProfile);

  useEffect(() => {
    if (draft.command) useTerminalConfigStore.getState().detect();
  }, [draft.command]);

  const candidate = useMemo(
    () => ({
      ...draft,
      args: argsText.split(/\r?\n/).map((a) => a.trim()).filter(Boolean),
      env: env.filter((e) => e.name || e.value),
      icon: draft.builtin ? draft.icon : lettermarkFor(draft.name || draft.command),
    }),
    [draft, argsText, env]
  );
  const errors = validateProfile(candidate);
  const hasErrors = Object.keys(errors).length > 0;
  const found = draft.command in detection ? detection[draft.command] : undefined;
  const problem = profileProblem(draft.command, detectionProblems[draft.command]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setEnvRow = (i, patch) => setEnv((rows) => withTrailingRow(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))));
  const err = (key) => (touched && errors[key] ? <div className="term-field-error">{t(`terminalExt.${errors[key]}`)}</div> : null);

  const save = () => {
    setTouched(true);
    if (hasErrors) return;
    upsert(candidate);
    onClose();
  };

  const cwdOptions = [
    { value: 'inherit', label: t('terminalExt.cwdInherit', "Current pane's folder, else the default folder") },
    { value: 'home', label: t('terminalExt.cwdHome', 'Home folder') },
    { value: 'fixed', label: t('terminalExt.cwdFixed', 'A fixed folder') },
    { value: 'ask', label: t('terminalExt.cwdAsk', 'Ask every time') },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={isNew ? t('terminalExt.newProfile', 'New profile') : t('terminalExt.editProfile', 'Edit {{name}}', { name: draft.name })}
      subtitle={t('terminalExt.profileSubtitle', 'A program to open in a terminal pane with one click')}
    >
      <div className="term-profile-form">
        <label className="term-field">
          <span className="modal-logo-label">{t('terminalExt.profileName', 'Name')}</span>
          <div className="term-field-inline">
            <PaneIcon pane={{ icon: candidate.icon, profileId: draft.builtin ? draft.id : undefined }} />
            <input
              className="modal-input w-full"
              value={draft.name}
              disabled={draft.builtin}
              onChange={(e) => set({ name: e.target.value })}
              placeholder={t('terminalExt.profileNamePh', 'e.g. Dev server')}
              autoFocus={isNew}
            />
          </div>
          {err('name')}
        </label>

        <label className="term-field">
          <span className="modal-logo-label">{t('terminalExt.profileCommand', 'Command')}</span>
          <div className="term-field-inline">
            <input
              className="modal-input w-full term-mono"
              value={draft.command}
              disabled={draft.builtin}
              onChange={(e) => set({ command: e.target.value.trim() })}
              placeholder="npx"
              spellCheck={false}
            />
            {!draft.builtin && (
              <button type="button" className="term-field-btn" onClick={async () => { const p = await pick(false); if (p) set({ command: p }); }}>
                <FolderOpen size={13} aria-hidden />
                {t('terminalExt.browse', 'Browse…')}
              </button>
            )}
          </div>
          {/* An app / shortcut is known from the path alone — no need to wait for detection (card 6). */}
          {(found !== undefined || problem) && draft.command && (
            <div className={`term-field-hint ${found && !problem ? 'term-field-hint--ok' : 'term-field-hint--bad'}`} role="status">
              {found && !problem ? <CheckCircle2 size={12} aria-hidden /> : <XCircle size={12} aria-hidden />}
              {found && !problem
                ? t('terminalExt.foundAt', 'Found at {{path}}', { path: found.path })
                : problemMessage(t, problem, draft.command) || t('terminalExt.notFoundOnPath', '{{command}} was not found on your PATH.', { command: draft.command })}
            </div>
          )}
          {err('command')}
        </label>

        <label className="term-field">
          <span className="modal-logo-label">{t('terminalExt.profileArgs', 'Arguments')}</span>
          <textarea
            className="modal-form-textarea term-mono"
            rows={3}
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            placeholder={t('terminalExt.profileArgsPh', 'One argument per line')}
            spellCheck={false}
          />
          <div className="term-field-hint">{t('terminalExt.profileArgsHint', 'Each line is passed as one argument, exactly as written — no shell parsing.')}</div>
        </label>

        <div className="term-field">
          <span className="modal-logo-label">{t('terminalExt.profileCwd', 'Working directory')}</span>
          <div className="term-field-inline">
            <CompactSelectPicker
              value={draft.cwd.mode}
              options={cwdOptions}
              onChange={(mode) => set({ cwd: { ...draft.cwd, mode } })}
              aria-label={t('terminalExt.profileCwd', 'Working directory')}
            />
            {draft.cwd.mode === 'fixed' && (
              <>
                <input
                  className="modal-input w-full term-mono"
                  value={draft.cwd.path || ''}
                  onChange={(e) => set({ cwd: { mode: 'fixed', path: e.target.value } })}
                  placeholder={'C:\\dev\\app'}
                  spellCheck={false}
                />
                <button type="button" className="term-field-btn" onClick={async () => { const p = await pick(true); if (p) set({ cwd: { mode: 'fixed', path: p } }); }}>
                  <FolderOpen size={13} aria-hidden />
                  {t('terminalExt.browse', 'Browse…')}
                </button>
              </>
            )}
          </div>
          {err('cwd')}
        </div>

        <div className="term-field">
          <span className="modal-logo-label">{t('terminalExt.profileEnv', 'Environment variables')}</span>
          <KvTable>
            {env.map((row, i) => (
              <KvRow
                key={i}
                enabled={row.enabled !== false}
                onToggleEnabled={(v) => setEnvRow(i, { enabled: v })}
                showRemove={i < env.length - 1}
                onRemove={() => setEnv((rows) => withTrailingRow(rows.filter((_, j) => j !== i)))}
                renderKey={
                  <input
                    className="request-builder-kv-input w-full rounded-md focus:outline-none term-mono"
                    value={row.name}
                    onChange={(e) => setEnvRow(i, { name: e.target.value })}
                    placeholder="NAME"
                    aria-label={t('terminalExt.envName', 'Variable name')}
                    spellCheck={false}
                  />
                }
                renderValue={
                  <input
                    className="request-builder-kv-input w-full rounded-md focus:outline-none term-mono"
                    value={row.value}
                    onChange={(e) => setEnvRow(i, { value: e.target.value })}
                    placeholder={t('terminalExt.envValue', 'value')}
                    aria-label={t('terminalExt.envValue', 'value')}
                    spellCheck={false}
                  />
                }
              />
            ))}
          </KvTable>
          {Object.keys(errors).filter((k) => k.startsWith('env.')).length > 0 && touched && (
            <div className="term-field-error">{t(`terminalExt.${errors[Object.keys(errors).find((k) => k.startsWith('env.'))]}`)}</div>
          )}
          <div className="term-field-hint">
            {t('terminalExt.profileEnvHint', "Stored in plain text on this device — don't put secrets here.")}
          </div>
        </div>

        <ProfileMcpSection draft={draft} onChange={set} />

        <div className="modal-footer-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={touched && hasErrors}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          >
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
